<template>
  <!-- 考试页面小窗模式 -->
  <div 
    v-if="isExamMode"
    v-show="modelValue"
    class="mini_player_container"
    :class="{ 'dragging': isDragging }"
    :style="{ left: position.x + 'px', top: position.y + 'px' }"
    @mousedown="startDrag"
  >
    <div class="mini_player_header">
      <span class="mini_player_title">媒体播放器</span>
      <div class="mini_player_controls">
        <button class="mini_control_btn" @click="toggleMinimize" :title="isMinimized ? '展开' : '最小化'">
          <span v-if="isMinimized">□</span>
          <span v-else>-</span>
        </button>
        <button class="mini_control_btn" @click="handleClose" title="关闭">×</button>
      </div>
    </div>
    <div 
      v-show="!isMinimized"
      v-loading="ctrl.loading"
      class="mini_video_content"
    >
      <div
        id="resource_player"
        ref="videoBox"
        class="mini_video_player"
      />
    </div>
  </div>

  <!-- 原有弹窗模式 -->
  <el-dialog
    v-else
    :model-value="modelValue"
    custom-class="preview_dialog"
    title=""
    :before-close="handleClose"
    :width="900"
    :append-to-body="true"
    :close-on-click-modal="false"
  >
    <div
      v-loading="ctrl.loading"
      class="video_body_content"
    >
      <div
        id="resource_player"
        ref="videoBox"
        class="video_win"
      />
    </div>
  </el-dialog>
</template>

<script>
import { reactive, ref, toRefs, computed } from 'vue'
import { materialPreView } from '@/api/paper.js'
export default defineComponent({
  name: 'Preview',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ''
    }
  },
  setup (props, { emit }) {
    const route = useRoute()
    const { id, modelValue } = toRefs(props)
    const videoBox = ref(null)
    
    // 检测是否为需要小窗模式的页面
    const isExamMode = computed(() => {
      const path = route.path
      
      // 考试相关页面
      if (path.includes('/exam/')) {
        return path.includes('/exam/startExam') || 
               path.includes('/exam/analysis') ||
               path.includes('/exam/result')
      }
      
      // 在线考试页面
      if (path.includes('/onlineExam/')) {
        return path.includes('/onlineExam/startExam') ||
               path.includes('/onlineExam/result')
      }
      
      // 练习相关页面
      if (path.includes('/practice/')) {
        return path.includes('/practice/startPractice') ||
               path.includes('/practice/analysis') ||
               path.includes('/practice/errorAnalysis') ||
               path.includes('/practice/error') ||
               path.includes('/practice/daily') ||
               path.includes('/practice/view')
      }
      
      // 入学测试页面
      if (path.includes('/entrancetest/')) {
        return path.includes('/entrancetest/exam')
      }
      
      // 成绩相关页面
      if (path.includes('/grade/')) {
        return path.includes('/grade/practice') ||
               path.includes('/grade/view') ||
               path.includes('/grade/homework')
      }
      
      return false
    })
    
    // 小窗模式相关状态
    const position = reactive({ x: 20, y: 100 })
    const isDragging = ref(false)
    const isMinimized = ref(false)
    const dragStart = reactive({ x: 0, y: 0 })
    
    const opts = reactive({
      imageList: []
    })
    const map = reactive({
      resource_player: null,
      vodPlatform: 2,
      userInfo: {}
    })
    const ctrl = reactive({
      loading: false
    })
    const handleClose = () => {
      if (map.resource_player) {
        map.resource_player.destroy()
        map.resource_player = null
      }
      emit('close')
    }
    
    // 小窗模式方法
    const startDrag = (e) => {
      if (e.target.closest('.mini_player_controls')) return
      
      isDragging.value = true
      dragStart.x = e.clientX - position.x
      dragStart.y = e.clientY - position.y
      
      const handleMouseMove = (e) => {
        if (isDragging.value) {
          position.x = e.clientX - dragStart.x
          position.y = e.clientY - dragStart.y
          
          // 边界检测
          const maxX = window.innerWidth - 300
          const maxY = window.innerHeight - 200
          
          position.x = Math.max(0, Math.min(position.x, maxX))
          position.y = Math.max(0, Math.min(position.y, maxY))
        }
      }
      
      const handleMouseUp = () => {
        isDragging.value = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
    
    const toggleMinimize = () => {
      isMinimized.value = !isMinimized.value
    }
    const getView = () => {
      ctrl.loading = true
      materialPreView({ id: id.value }).then(res => {
        map.vodPlatform = res.vodPlatform
        if (res.videoConfig) {
          const c = JSON.parse(res.videoConfig)
          res = {
            ...res,
            ...c,
            videoConfig: c
          }
        }
        if (res.vodPlatform === 1 || res.vodPlatform === 4 || res.vodPlatform === 10) {
          play(res)
        } else if (res.vodPlatform === 2) {
          baiJiaYunPlay(res)
        } if (res.vodPlatform === 3) {
          if (res.videoConfig) {
            ccPlay(res.videoConfig)
          }
        }
        ctrl.loading = false
      })
    }

    // 加载CCjs
    const loadCCScript = (url, callback) => {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      // 处理IE
      if (script.readyState) {
        script.onreadystatechange = function () {
          if (script.readyState === 'loaded' || script.readyState === 'complete') {
            script.onreadystatechange = null
            callback()
          }
        }
      } else {
        // 处理其他浏览器的情况
        script.onload = function () {
          callback()
        }
      }
      script.src = url
      document.body.append(script)
    }

    const ccPlay = (videoConfig) => {
      const playerDom = document.getElementById('resource_player')
      playerDom.innerHTML = ''
      // 获得场景 CC
      if (window.ccH5PlayerJsLoaded) {
        map.resource_player = window.createCCH5Player({
          vid: videoConfig.vid,
          vc: videoConfig.vc,
          siteid: videoConfig.siteid,
          autoStart: true,
          width: '100%',
          height: '100%',
          parentNode: playerDom
        })
      }
      window.onCCH5PlayerLoaded = () => {
        window.ccH5PlayerJsLoaded = true
        map.resource_player = window.createCCH5Player({
          vid: videoConfig.vid,
          vc: videoConfig.vc,
          siteid: videoConfig.siteid,
          autoStart: true,
          width: '100%',
          height: '100%',
          parentNode: playerDom
        })
      }
      loadCCScript(`https://p.bokecc.com/player?siteid=${videoConfig.siteid}&newversion=true`, () => {
        console.log('加载成功')
      })
    }

    const baiJiaYunPlay = (data) => {
      const _that = map
      const box = videoBox.value
      let marquee = null
      if (data.vodMarqueeContent) {
        marquee = {
          displayMode: 'roll', // 展示形式滚动roll 或者闪烁 blink
          rollDuration: 10, // 滚动间隔时长 单位秒
          blinkDuration: 10, // 闪烁间隔时长 单位秒
          type: 1, // 1 读取value设置的固定值 2 读取 user.name 昵称
          count: 1, // 跑马灯单双数 1 单数 2 双数
          color: '#ffffff', // 跑马灯颜色
          fontOpacity: 1, // 跑马灯透明度 0 - 1
          fontSize: 20, // 跑马灯字体大小 单位px
          fontBold: 0, // 跑马灯文字加粗 0 不加粗 1 加粗
          backgroundColor: '#999999', // 跑马灯背景色 默认#1795ff
          backgroundOpacity: 0.4, // 跑马灯背景透明度 0 - 1
          position: 'random', // 跑马灯位置 random 随机 top 顶部 bottom 底部
          value: data.vodMarqueeContent // 跑马灯固定值,
        }
      }
      document.querySelector('#resource_player').innerHTML = ''
      const playerConfig = {
        container: document.querySelector('#resource_player'),
        width: box.offsetWidth,
        height: 500,
        token: data.token,
        autoplay: false,
        vid: data.vid,
        memory: false,
        playbackRates: [],
        showCurrentTimeDisplay: true,
        showDurationDisplay: true,
        disableSeek: 0, // 是否禁止拖拽， 0：可以， 1：禁止
        user: {
          number: _that.userInfo.userNo || '',
          name: _that.userInfo.userName || ''
        },
        plugins: marquee ? { marquee } : {}
      }
      console.log('previewResource - 百家云播放器初始化参数:', playerConfig)
      _that.resource_player = new BPlayer(playerConfig)
      setTimeout(() => {
        _that.resource_player.play()
      }, 500)
    }
    const play = (data, startTime = 0) => {
      const box = videoBox.value
      if (!box) {
        play(data, startTime)
        return
      }
      if (map.resource_player) {
        const param = {
          watchStartTime: startTime || 0,
          width: box.offsetWidth,
          height: 500,
          autoplay: false
        }
        if (data.vodPlatform === 4) {
          param.url = data.videoUrl
        } else {
          param.ts = data.ts
          param.sign = data.sign
          param.vid = data.vid
          param.playsafe = data.token
          param.code = data.code
        }
        map.resource_player.changeVid(param)
      } else {
        document.querySelector('#resource_player').innerHTML = ''
        const param = {
          wrap: '#resource_player',
          width: box.offsetWidth,
          height: 500,
          history_video_duration: 2,
          forceH5: true,
          hideSwitchPlayer: true,
          autoplay: false,
          speed: true,
          watchStartTime: startTime,
          viewerInfo: {
            viewerId: map.userInfo.userNo,
            viewerName: map.userInfo.userName
          }
        }
        if (data.vodPlatform === 4) {
          param.url = data.videoUrl
        } else {
          param.ts = data.ts
          param.sign = data.sign
          param.vid = data.vid
          param.playsafe = data.token
          param.code = data.code
        }
        console.log('previewResource - 保利威播放器初始化参数:', param)
        map.resource_player = window.polyvPlayer(param)
      }
    }
    if (modelValue.value) {
      if (id.value) {
        getView()
      }
    }
    return {
      opts,
      ctrl,
      modelValue,
      videoBox,
      handleClose,
      isExamMode,
      position,
      isDragging,
      isMinimized,
      startDrag,
      toggleMinimize
    }
  }
})
</script>
<style lang="scss">
.preview_dialog {
  .el-dialog__footer {
    padding: 0;
  }
}

/* 小窗播放器样式 */
.mini_player_container {
  position: fixed;
  width: 300px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  user-select: none;
  
  &.dragging {
    cursor: move;
  }
}

.mini_player_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px 8px 0 0;
  cursor: move;
  border-bottom: 1px solid #ddd;
}

.mini_player_title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.mini_player_controls {
  display: flex;
  gap: 8px;
}

.mini_control_btn {
  width: 24px;
  height: 24px;
  border: none;
  background: #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: #666;
  
  &:hover {
    background: #d0d0d0;
    color: #333;
  }
}

.mini_video_content {
  padding: 0;
  height: 180px;
  background: #000;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.mini_video_player {
  width: 100%;
  height: 100%;
}
</style>
<style scoped>
.video_body_content{
  position: relative;
  min-height: 500px;
}
.video_win {
  height: 500px;
}
</style>
